import { PayloadAction, createSlice } from "@reduxjs/toolkit";
import { RootState } from "app/store";
import { ApiResponse } from "models";
import { DocumentState } from "models/document";

const initialState: DocumentState = {
  documentloading: false,
  loading: false,
  data: {},
  customerListData: [],
  customerListSuccess: false,
  customerListMessage: "",
  addDocumentMessage: "",
  message: "",
  success: false,
  documentUrl: "",
  updateMessage: "",
  addDocumentLoading: false,
  updateDocumentMessage: "",
  updateDocumentLoading: false,
  brandListData: [],
  brandListSuccess: false,
  brandListMessage: false,
  deleteLoading: false,
  deleteMessage: "",
  customerListLoading: false,
  storeListData: [],
  storeListDataLoading: false,
  storeListDataMessage: "",
  storeListDataSuccess: false,
  documentByIdLoading: false,
  documentByIdSuccess: false,
  documentByIdData: [],
};

const documentSlice = createSlice({
  name: "document",
  initialState,
  reducers: {
    getDocument(state, action: PayloadAction<ApiResponse>) {
      state.loading = true;
      state.message = "";
    },
    getDocumentSuccess(state, action: PayloadAction<any>) {
      state.loading = false;
      state.data = action.payload;
      state.success = action.payload.success;
    },
    getDocumentFailed(state, action: PayloadAction<ApiResponse>) {
      state.loading = false;
      state.data = {};
      state.message = action.payload?.message ?? "Something went wrong";
      state.success = action.payload?.success ?? false;
    },
    getDocumentById(state, action: PayloadAction<ApiResponse>) {
      state.documentByIdLoading = true;
      state.documentByIdSuccess = false;
    },
    getDocumentByIdSuccess(state, action: PayloadAction<any>) {
      state.documentByIdLoading = false;
      state.documentByIdData = action.payload.data;
      state.documentByIdSuccess = action.payload.success;
    },
    getDocumentByIdFailed(state, action: PayloadAction<ApiResponse>) {
      state.documentByIdLoading = false;
      state.documentByIdData = {};
      state.documentByIdSuccess = action.payload?.success ?? false;
    },
    addDocument(state, action: PayloadAction<ApiResponse>) {
      state.addDocumentLoading = true;
      state.addDocumentMessage = "";
    },
    addDocumentSuccess(state, action: PayloadAction<ApiResponse>) {
      state.addDocumentLoading = false;
      state.addDocumentMessage = action.payload.message;
      state.success = action.payload.success;
    },
    addDocumentFailed(state, action: PayloadAction<ApiResponse>) {
      state.addDocumentLoading = false;
      state.addDocumentMessage = action.payload?.message ?? "Something went wrong";
      state.success = action.payload?.success ?? false;
    },
    updateDocument(state, action: PayloadAction<ApiResponse>) {
      state.updateDocumentLoading = true;
      state.updateDocumentMessage = "";
    },
    updateDocumentSuccess(state, action: PayloadAction<ApiResponse>) {
      state.updateDocumentLoading = false;
      state.updateDocumentMessage = action.payload.message;
      state.success = action.payload.success;
    },
    updateDocumentFailed(state, action: PayloadAction<ApiResponse>) {
      state.updateDocumentLoading = false;
      state.updateDocumentMessage = action.payload?.message ?? "Something went wrong";
      state.success = action.payload?.success ?? false;
    },
    deleteDocument(state, action: PayloadAction<ApiResponse>) {
      state.deleteLoading = true;
      state.deleteMessage = "";
    },
    deleteDocumentSuccess(state, action: PayloadAction<ApiResponse>) {
      state.deleteLoading = false;
      state.deleteMessage = action.payload.message;
      state.success = action.payload.success;
    },
    deleteDocumentFailed(state, action: PayloadAction<ApiResponse>) {
      state.deleteLoading = false;
      state.deleteMessage = action.payload?.message ?? "Something went wrong";
      state.success = action.payload?.success ?? false;
    },
    getCustomerList(state, action: PayloadAction<ApiResponse>) {
      state.customerListLoading = true;
      state.customerListMessage = "";
    },
    getCustomerListSuccess(state, action: PayloadAction<ApiResponse>) {
      state.customerListLoading = false;
      state.customerListData = action.payload?.CustomerData;
      state.customerListSuccess = action.payload.success;
    },
    getCustomerListFailed(state, action: PayloadAction<ApiResponse>) {
      state.customerListLoading = false;
      state.customerListMessage = action.payload?.message ?? "Something went wrong";
      state.customerListSuccess = action.payload?.success ?? false;
    },
    getBrandList(state, action: PayloadAction<ApiResponse>) {
      state.customerListLoading = true;
      state.customerListMessage = "";
    },
    getBrandListSuccess(state, action: PayloadAction<ApiResponse>) {
      state.customerListLoading = false;
      state.brandListData = action.payload.data;
      state.customerListSuccess = action.payload.success;
    },
    getBrandListFailed(state, action: PayloadAction<ApiResponse>) {
      state.customerListLoading = false;
      state.brandListData = [];
      state.customerListMessage = action.payload?.message ?? "Something went wrong";
      state.customerListSuccess = action.payload?.success ?? false;
    },
    getStoreList(state, action: PayloadAction<ApiResponse>) {
      state.storeListDataLoading = true;
      state.storeListDataMessage = "";
    },
    getStoreListSuccess(state, action: PayloadAction<ApiResponse>) {
      state.storeListDataLoading = false;
      state.storeListData = action.payload.storeName;
      state.storeListDataSuccess = action.payload.success;
    },
    getStoreListFailed(state, action: PayloadAction<ApiResponse>) {
      state.storeListDataLoading = false;
      state.storeListDataMessage = action.payload?.message ?? "Something went wrong";
      state.storeListDataSuccess = action.payload?.success ?? false;
    },
    getDocumentUrl(state, action: PayloadAction<ApiResponse>) {
      state.documentloading = true;
      state.documentUrl = "";
    },
    getDocumentUrlSuccess(state, action: PayloadAction<ApiResponse>) {
      state.documentloading = false;
      state.documentUrl = action.payload.documentURL;
    },
    getDocumentUrlFailed(state, action: PayloadAction<ApiResponse>) {
      state.documentloading = false;
      state.documentUrl = "";
      state.message = action.payload?.message ?? "Something went wrong";
    },
    clearData(state) {
      state.loading = false;
      state.message = "";
      state.success = false;
      state.addDocumentLoading = false;
      state.addDocumentMessage = "";
      state.updateMessage = "";
      state.deleteMessage = "";
      state.documentUrl = "";
      state.updateDocumentMessage = "";
      state.updateDocumentLoading = false;
      state.deleteMessage = "";
      state.deleteLoading = false;
      state.storeListDataSuccess = false;
      state.storeListDataMessage = "";
    },
  },
});

//Actions
export const documentActions = documentSlice.actions;

//Selectors
export const getDocumentData = (state: RootState) => state.document.data;
export const getDocumentById = (state: RootState) =>
  state.document.documentByIdData;
export const getDocumentDataLoad = (state: RootState) => state.document.loading;
export const addDocumentIsLoading = (state: RootState) =>
  state.document.addDocumentLoading;
export const addDocumentIsMessage = (state: RootState) =>
  state.document.addDocumentMessage;
export const CustomerListData = (state: RootState) =>
  state.document.customerListData;
export const BrandListData = (state: RootState) => state.document.brandListData;
export const StoreListData = (state: RootState) => state.document.storeListData;
export const updateDocumentMessage = (state: RootState) =>
  state.document.updateDocumentMessage;
export const updateDocumentLoading = (state: RootState) =>
  state.document.updateDocumentLoading;
export const deleteDocumentLoad = (state: RootState) =>
  state.document.deleteLoading;
export const deleteDocumentMsg = (state: RootState) =>
  state.document.deleteMessage;
export const brandListData = (state: RootState) =>
  state.brandLocation.brandListData;
export const documnetLoading = (state: RootState) =>
  state.document.documentloading;
export const documentUrlBack = (state: RootState) => state.document.documentUrl;

//Reducers
const documentReducer = documentSlice.reducer;
export default documentReducer;
